"use client";

import {
	RiHeartFill,
	RiStarFill,
	RiStarLine,
} from "@remixicon/react";
import type { ProcessedSet, Review } from "@workspace/types";
import {
	getB2BDisplayParts,
	isProgramSet,
	parseGenres,
	parseSpecialGuestsForBadges,
	setHasSpecialGuests,
} from "@workspace/types";
import { Button } from "@workspace/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import { Label } from "@workspace/ui/components/label";
import { cn } from "@workspace/ui/lib/utils";
import Link from "next/link";
import { Fragment, memo, useEffect, useRef } from "react";
import { GenreBadgeList } from "@/components/shared/genre-badge-list";
import { StatusBadge } from "@/components/shared/status-badge";
import { ArtistPhoto } from "@/components/shared/photos/artist-photo";
import {
	formatDayAndTime,
	formatStageName,
} from "@/lib/business-logic/set-rendering";
import { createArtistSlug } from "@/lib/features/artist/artist-utils";
import { isSetPast } from "@/lib/utils/date/formatting";

// Utility function to extract YouTube video ID from various URL formats
const extractYouTubeVideoId = (url: string): string | null => {
	const patterns = [
		/youtu\.be\/([a-zA-Z0-9_-]{11})/,
		/youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/,
		/youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
		/youtube\.com\/v\/([a-zA-Z0-9_-]{11})/,
		/youtube\.com\/live\/([a-zA-Z0-9_-]{11})/,
	];

	for (const pattern of patterns) {
		const match = url.match(pattern);
		if (match?.[1]) return match[1];
	}
	return null;
};

interface ReadOnlyStarsProps {
	rating: number;
	loved?: boolean;
	className?: string;
}

const ReadOnlyStars = ({
	rating,
	loved = false,
	className,
}: ReadOnlyStarsProps) => {
	// Convert rating from 0-10 to 0-5 scale for display
	const displayRating = rating > 0 ? rating / 2 : 0;

	// Calculate whole stars and whether there's a half star
	const wholeStars = Math.floor(displayRating);
	const hasHalfStar = displayRating % 1 >= 0.5;

	return (
		<div className={cn("flex items-center", className)}>
			{/* Stars */}
			<div className="flex items-center gap-1">
				{[...Array(5)].map((_, i) => {
					const starPosition = i + 1;

					if (starPosition <= wholeStars) {
						// Full star
						return <RiStarFill key={i} className="h-6 w-6 text-yellow-500" />;
					}
					if (starPosition === wholeStars + 1 && hasHalfStar) {
						// Half star
						return (
							<div key={i} className="relative">
								<RiStarLine className="h-6 w-6 text-muted-foreground/50" />
								<div className="absolute top-0 left-0 w-[50%] overflow-hidden">
									<RiStarFill className="h-6 w-6 text-yellow-500" />
								</div>
							</div>
						);
					}
					// Empty star
					return (
						<RiStarLine key={i} className="h-6 w-6 text-muted-foreground/50" />
					);
				})}
			</div>

			{/* Vertical separator and heart if loved */}
			{loved && (
				<>
					<div className="mx-1.5 h-6 w-px bg-border/50" />
					<RiHeartFill className="h-6 w-6 text-red-500" />
				</>
			)}
		</div>
	);
};

interface SetReviewModalReadOnlyProps {
	isOpen: boolean;
	set: ProcessedSet | null;
	review: Review | null;
	festivalSlug?: string; // Optional for backward compatibility
	onClose: () => void;
}

export const SetReviewModalReadOnly = memo(
	({
		isOpen,
		set,
		review,
		festivalSlug,
		onClose,
	}: SetReviewModalReadOnlyProps) => {
		const isMountedRef = useRef(true);

		useEffect(() => {
			isMountedRef.current = true;
			return () => {
				isMountedRef.current = false;
			};
		}, []);

		// Enhanced onClose handler with cleanup guard
		const handleClose = () => {
			if (isMountedRef.current) {
				onClose();
			}
		};

		if (!set) {
			return null; // Don't render if set data is missing
		}

		// Check if this is a program set
		const isProgram = isProgramSet(set);

		return (
			<Dialog open={isOpen} onOpenChange={handleClose}>
				<DialogContent className="max-h-[90vh] w-full max-w-[95%] overflow-auto p-0 sm:max-w-md md:max-w-lg">
					<DialogTitle className="sr-only">Review Set</DialogTitle>
					<DialogDescription className="sr-only">
						View details and ratings for this set performance
					</DialogDescription>
					<DialogHeader className="space-y-4 px-6 pt-6 pb-0">
						{/* Artist Photo and Title Layout */}
						<div className="flex gap-3">
							{/* Artist Photo */}
							<div className="flex-shrink-0">
								<ArtistPhoto
									name={set.alias_name || set.artist?.name || "Unknown Artist"}
									imageUrl={set.artist?.image_url}
									size="lg"
									rounded="card"
									aspectRatio="square"
									isProgram={isProgram}
								/>
							</div>

							{/* Title and Info */}
							<div className="min-w-0 flex-1">
								<h3 className="font-semibold leading-none tracking-tight">
									{(() => {
										// Handle program sets differently
										if (
											isProgram &&
											"_program_set" in set &&
											set._program_set
										) {
											return set._program_set.name;
										}

										const displayParts = getB2BDisplayParts(set, "review");

										if (
											displayParts.isB2B ||
											displayParts.isPresents ||
											displayParts.isFriends
										) {
											if (displayParts.parts) {
												// For 3+ artist collaborations, render each part individually with spacing
												return (
													<>
														{/* Main display with alias names */}
														{displayParts.parts.map((part, index) => (
															<Fragment key={index}>
																{part.type === "artist" ||
																part.type === "presents" ? (
																	part.text
																) : (
																	<span className="font-normal text-muted-foreground/50">
																		{part.text}
																	</span>
																)}
																{index <
																	(displayParts.parts?.length ?? 0) - 1 && " "}
															</Fragment>
														))}

														{/* Muted display with real names if using alias */}
														{displayParts.parts.some(
															(part) => part.realText,
														) && (
															<span className="ml-1 font-normal text-muted-foreground/50">
																{displayParts.parts.map((part, index) => (
																	<Fragment key={`real-${index}`}>
																		{part.type === "artist"
																			? part.realText || part.text
																			: part.type === "presents"
																				? part.text
																				: part.text}
																		{index <
																			(displayParts.parts?.length ?? 0) - 1 &&
																			" "}
																	</Fragment>
																))}
															</span>
														)}
													</>
												);
											}
											// For 2-artist collaborations, presents, and friends sets
											return (
												<>
													{/* Main display */}
													{displayParts.mainText}
													{displayParts.connector && (
														<span className="ml-1 font-normal text-muted-foreground/50">
															{displayParts.connector}
														</span>
													)}
													{displayParts.secondaryText && (
														<span className="ml-1">
															{displayParts.secondaryText}
														</span>
													)}

													{/* Muted display with real names if using alias */}
													{displayParts.realMainText && (
														<span className="ml-1 font-normal text-muted-foreground/50">
															{/* For group aliases, show only additional collaborators */}
															{set.is_group_alias
																? (() => {
																		// Extract collaborator names, excluding the primary artist that's already shown in the alias
																		const allNames =
																			displayParts.realMainText.split(" & ");
																		const primaryArtistName = set.artist?.name;
																		const collaboratorNames = allNames.filter(
																			(name) => name !== primaryArtistName,
																		);
																		return collaboratorNames.length > 0
																			? `(${collaboratorNames.join(" & ")})`
																			: null;
																	})()
																: displayParts.realMainText}
															{!set.is_group_alias &&
																displayParts.connector && (
																	<span className="ml-1">
																		{displayParts.connector}
																	</span>
																)}
															{!set.is_group_alias &&
																displayParts.secondaryText && (
																	<span className="ml-1">
																		{displayParts.secondaryText}
																	</span>
																)}
														</span>
													)}
												</>
											);
										}

										// Handle existing alias system for non-B2B sets
										if (
											set.alias_name &&
											set.artist &&
											set.alias_name !== set.artist.name
										) {
											return (
												<>
													{set.alias_name}{" "}
													<span className="ml-1 font-normal text-muted-foreground/50">
														{set.artist.name}
													</span>
												</>
											);
										}

										return set.alias_name || set.artist?.name || "?????";
									})()}
								</h3>

								{/* Stage and Time - smaller font, reduced gap */}
								<p className="text-muted-foreground text-xs">
									{formatStageName(set)}
								</p>
								<p className="text-muted-foreground text-xs">
									{formatDayAndTime(set)}
								</p>
							</div>
						</div>
					</DialogHeader>
					<div className="px-6">
						<div className="space-y-4">
							{/* Genres section - moved from header */}
							{(() => {
								// Get genres - only for artist sets, not program sets
								let genreArray: string[] = [];

								if (!isProgram) {
									if (set.is_collaborative && set.set_artists) {
										const allGenres = new Set<string>();
										for (const setArtist of set.set_artists) {
											if (setArtist.artist.spotify_genres) {
												const genres = parseGenres(
													setArtist.artist.spotify_genres,
													setArtist.artist.manual_genres,
												);
												for (const genre of genres) {
													allGenres.add(genre);
												}
											}
										}
										genreArray = Array.from(allGenres);
									} else if (set.artist?.spotify_genres) {
										genreArray = parseGenres(
											set.artist.spotify_genres,
											set.artist.manual_genres,
										);
									}
								}

								const hasGenres = genreArray.length > 0;

								// Don't render anything if no genres
								if (!hasGenres) return null;

								return (
									<div className="space-y-1">
										<Label>Genres</Label>
										<GenreBadgeList genres={genreArray} className="gap-2" />
									</div>
								);
							})()}

							{/* Special Guests section - moved from header */}
							{!isProgram && setHasSpecialGuests(set) && (
								<div className="space-y-1">
									<Label>Special Guests</Label>
									<div className="flex flex-wrap gap-1">
										{parseSpecialGuestsForBadges(
											set.special_guests || null,
										).map((guest, index) => (
											<Link
												key={index}
												href={`/artists/${createArtistSlug(guest)}`}
												className="cursor-pointer rounded-md bg-primary/10 px-2 py-1 font-medium text-primary text-xs transition-colors duration-200 hover:bg-primary/20"
											>
												{guest}
											</Link>
										))}
									</div>
								</div>
							)}
							{/* YouTube Player */}
							{set.youtube_url &&
								(() => {
									const videoId = extractYouTubeVideoId(set.youtube_url);
									if (!videoId) return null;

									return (
										<div className="overflow-hidden rounded-lg bg-black">
											<iframe
												src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`}
												width="100%"
												height="240"
												style={{ border: 0 }}
												allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
												allowFullScreen
												title={`${set.alias_name || set.artist?.name || "Unknown"} - Live Stream`}
												className="w-full"
											/>
										</div>
									);
								})()}

							{/* Review content - show for all sets with reviews */}
							{review && (
								<>
									{/* Rating and Status badges in two columns */}
									{review.rating > 0 ||
									review.loved ||
									review.mustSee ||
									review.wantToSee ||
									review.sawLive ||
									review.sawStream ? (
										<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
											{/* Left column - Rating */}
											{(review.rating > 0 || review.loved) && (
												<div className="space-y-2">
													<Label>Rating</Label>
													<ReadOnlyStars
														rating={review.rating || 0}
														loved={review.loved}
													/>
												</div>
											)}

											{/* Right column - Status badges */}
											{(review.mustSee ||
												review.wantToSee ||
												review.sawLive ||
												review.sawStream) && (
												<div className="space-y-2">
													<Label>Status</Label>
													<div className="flex flex-wrap gap-2">
														{review?.mustSee && (
															<StatusBadge status="mustSee" size="sm" />
														)}
														{review?.wantToSee &&
															!review?.mustSee &&
															!review?.sawLive &&
															!review?.sawStream && (
																<StatusBadge
																	status="wantToSee"
																	size="sm"
																	label={isSetPast(set) ? "Wanted to See" : "Want to See"}
																/>
															)}
														{review?.sawLive && (
															<StatusBadge status="sawLive" size="sm" />
														)}
														{review?.sawStream && (
															<StatusBadge status="sawStream" size="sm" />
														)}
													</div>
												</div>
											)}
										</div>
									) : null}

									{/* Review text */}
									{review?.content && (
										<div className="rounded-lg bg-muted p-3 text-sm">
											{review.content}
										</div>
									)}
								</>
							)}

							{/* Program set description */}
							{isProgram &&
								"_program_set" in set &&
								set._program_set?.description && (
									<div className="rounded-lg bg-muted p-3 text-sm">
										{set._program_set.description}
									</div>
								)}
						</div>
					</div>
					<DialogFooter className="px-6 pt-0 pb-6">
						<div className="flex w-full items-center justify-between">
							{/* View Details button - left side */}
							{festivalSlug && set && (
								<Button variant="secondary" asChild>
									<Link href={`/festivals/${festivalSlug}/${set.id}`}>
										View Details
									</Link>
								</Button>
							)}
							{(!festivalSlug || !set) && <div />}{" "}
							{/* Spacer when no View Details button */}
							{/* Close button - right side */}
							<Button onClick={handleClose}>Close</Button>
						</div>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		);
	},
);

SetReviewModalReadOnly.displayName = "SetReviewModalReadOnly";
